import type {
  HoverIdleAutoResetExecutionEvent,
  HoverIdleHovGainUpgradeMpAutomationEvent,
} from "@/features/hoverIdle/core/hoverIdleModel";
import type {
  HoverIdleSnapshot,
  HovCellGainUpgradeIndex,
  HovGainUpgradeMpTarget,
  NHovBranchId,
} from "@/features/hoverIdle/types";
import { toHovGainUpgradeMpTargetKey } from "@/features/hoverIdle/mpm/mpmHovGainUpgradeTargets";
import {
  compareHuge,
  hugeZero,
  serializeHuge,
  type HugeNumber,
} from "@/shared/numbers";

type OHovAutomationEvent = Extract<
  HoverIdleAutoResetExecutionEvent,
  { readonly layer: "ohov" }
>;

export interface GameplayAutomationBurstOrderedEvent {
  readonly sequence: number;
  readonly kind:
    | HoverIdleAutoResetExecutionEvent["kind"]
    | HoverIdleHovGainUpgradeMpAutomationEvent["kind"];
  readonly branchId?: NHovBranchId;
  readonly hguPurchaseCount?: number;
}

export interface GameplayAutomationBurstSegmentSummary {
  readonly firstSequence: number;
  readonly lastSequence: number;
  readonly resetEventsTotal: number;
  readonly mHovCount: number;
  readonly nHovCount: number;
  readonly nHovCountsByBranch: Readonly<Partial<Record<NHovBranchId, number>>>;
  readonly nHovBranchLevelGains: Readonly<
    Partial<Record<NHovBranchId, number>>
  >;
  readonly hguSegmentCount: number;
  readonly hguPurchaseCount: number;
  readonly hguAttempts: number;
  readonly hovSpentByHgu: string;
  readonly unlockedTargetIndexes: readonly HovCellGainUpgradeIndex[];
  readonly peakHovLog10: number | null;
  readonly peakHovGainPerSecondLog10: number | null;
  readonly orderedEvents: readonly GameplayAutomationBurstOrderedEvent[];
}

export interface GameplayAutomationBurstTotals {
  readonly resetEventsTotal: number;
  readonly mHovCount: number;
  readonly nHovCount: number;
  readonly oHovCount: number;
  readonly hguSegmentCount: number;
  readonly hguPurchaseCount: number;
  readonly unlockedTargetIndexes: readonly HovCellGainUpgradeIndex[];
}

export type GameplayAutomationBurstPlayLogItem =
  | {
      readonly kind: "summary";
      readonly summary: GameplayAutomationBurstSegmentSummary;
    }
  | {
      readonly kind: "ohov";
      readonly event: OHovAutomationEvent;
    };

export interface GameplayAutomationBurstSummary {
  readonly totals: GameplayAutomationBurstTotals;
  readonly playLogItems: readonly GameplayAutomationBurstPlayLogItem[];
}

export interface GameplayAutomationHguMilestone {
  readonly target: HovGainUpgradeMpTarget;
  readonly isMilestone: true;
  readonly enhancementLevelBefore: number;
  readonly enhancementLevelAfter: number;
}

export interface CommitGameplayAutomationBurstSideEffectPorts {
  readonly recordPlayLogItem: (
    item: GameplayAutomationBurstPlayLogItem,
  ) => void;
  readonly recordHguMilestones: (
    milestones: readonly GameplayAutomationHguMilestone[],
    snapshot: HoverIdleSnapshot,
  ) => void;
  readonly pulseHguTarget: (
    target: HovGainUpgradeMpTarget,
    isMilestone: boolean,
  ) => void;
  readonly notifyOHovWallClears: (
    events: readonly OHovAutomationEvent[],
    finalSnapshot: HoverIdleSnapshot,
  ) => boolean;
  readonly refreshView: (snapshot: HoverIdleSnapshot) => void;
  readonly requestAutosave: () => void;
  readonly getFinalSnapshot: () => HoverIdleSnapshot;
}

export function summarizeGameplayAutomationBurst(input: {
  readonly resetEvents: readonly HoverIdleAutoResetExecutionEvent[];
  readonly hguEvents: readonly HoverIdleHovGainUpgradeMpAutomationEvent[];
}): GameplayAutomationBurstSummary {
  const ordered = [
    ...input.resetEvents.map((event) => ({
      kind: "reset" as const,
      sequence: event.sequence,
      event,
    })),
    ...input.hguEvents.map((event) => ({
      kind: "hgu" as const,
      sequence: event.sequence,
      event,
    })),
  ].sort((a, b) => a.sequence - b.sequence);

  const playLogItems: GameplayAutomationBurstPlayLogItem[] = [];
  let segment = createSegmentAccumulator();
  const flushSegment = (): void => {
    const summary = finalizeSegment(segment);
    if (summary !== null) {
      playLogItems.push({ kind: "summary", summary });
    }
    segment = createSegmentAccumulator();
  };

  for (const item of ordered) {
    if (item.kind === "reset") {
      if (item.event.layer === "ohov") {
        flushSegment();
        playLogItems.push({ kind: "ohov", event: item.event });
        continue;
      }
      accumulateResetEvent(segment, item.event);
    } else {
      accumulateHguEvent(segment, item.event);
    }
  }
  flushSegment();

  const unlockedTargetIndexes = collectUnlockedTargetIndexes(input.hguEvents);
  return {
    totals: {
      resetEventsTotal: input.resetEvents.length,
      mHovCount: input.resetEvents.filter(
        (event) => event.kind === "mhov-mp-execution",
      ).length,
      nHovCount: input.resetEvents.filter(
        (event) => event.kind === "nhov-mp-execution",
      ).length,
      oHovCount: input.resetEvents.filter((event) => event.layer === "ohov")
        .length,
      hguSegmentCount: input.hguEvents.length,
      hguPurchaseCount: sumHguPurchases(input.hguEvents),
      unlockedTargetIndexes,
    },
    playLogItems,
  };
}

export function commitGameplayAutomationBurstSideEffects(input: {
  readonly resetEvents: readonly HoverIdleAutoResetExecutionEvent[];
  readonly hguEvents: readonly HoverIdleHovGainUpgradeMpAutomationEvent[];
  readonly finalSnapshot: HoverIdleSnapshot;
  readonly ports: CommitGameplayAutomationBurstSideEffectPorts;
}): HoverIdleSnapshot {
  const summary = summarizeGameplayAutomationBurst(input);
  const hasStateChanges =
    summary.totals.resetEventsTotal > 0 || summary.totals.hguPurchaseCount > 0;
  if (!hasStateChanges) {
    return input.finalSnapshot;
  }

  for (const item of summary.playLogItems) {
    input.ports.recordPlayLogItem(item);
  }

  const milestones = collectHguMilestones(input.hguEvents);
  if (milestones.length > 0) {
    input.ports.recordHguMilestones(milestones, input.finalSnapshot);
  }
  for (const pulse of collectHguPulses(input.hguEvents)) {
    input.ports.pulseHguTarget(pulse.target, pulse.isMilestone);
  }

  const oHovEvents = input.resetEvents.filter(
    (event): event is OHovAutomationEvent => event.layer === "ohov",
  );
  const markedWallClearSeen =
    oHovEvents.length > 0 &&
    input.ports.notifyOHovWallClears(oHovEvents, input.finalSnapshot);
  const activeSnapshot = markedWallClearSeen
    ? input.ports.getFinalSnapshot()
    : input.finalSnapshot;

  input.ports.refreshView(activeSnapshot);
  input.ports.requestAutosave();
  return activeSnapshot;
}

interface SegmentAccumulator {
  firstSequence: number | null;
  lastSequence: number | null;
  resetEventsTotal: number;
  mHovCount: number;
  nHovCount: number;
  readonly nHovCountsByBranch: Partial<Record<NHovBranchId, number>>;
  readonly nHovBranchLevelGains: Partial<Record<NHovBranchId, number>>;
  hguSegmentCount: number;
  hguPurchaseCount: number;
  hguAttempts: number;
  hovSpentByHgu: HugeNumber;
  readonly unlockedTargetIndexes: Set<HovCellGainUpgradeIndex>;
  peakHovLog10: number | null;
  peakHovGainPerSecondLog10: number | null;
  readonly orderedEvents: GameplayAutomationBurstOrderedEvent[];
}

function createSegmentAccumulator(): SegmentAccumulator {
  return {
    firstSequence: null,
    lastSequence: null,
    resetEventsTotal: 0,
    mHovCount: 0,
    nHovCount: 0,
    nHovCountsByBranch: {},
    nHovBranchLevelGains: {},
    hguSegmentCount: 0,
    hguPurchaseCount: 0,
    hguAttempts: 0,
    hovSpentByHgu: hugeZero(),
    unlockedTargetIndexes: new Set(),
    peakHovLog10: null,
    peakHovGainPerSecondLog10: null,
    orderedEvents: [],
  };
}

function accumulateResetEvent(
  segment: SegmentAccumulator,
  event: Exclude<HoverIdleAutoResetExecutionEvent, { readonly layer: "ohov" }>,
): void {
  observeSequence(segment, event.sequence);
  segment.resetEventsTotal += 1;
  segment.orderedEvents.push({
    sequence: event.sequence,
    kind: event.kind,
    ...(event.kind === "nhov-mp-execution" ? { branchId: event.branchId } : {}),
  });
  segment.peakHovLog10 = maxNullable(
    segment.peakHovLog10,
    toSafeLog10(event.before.hov),
  );
  segment.peakHovGainPerSecondLog10 = maxNullable(
    segment.peakHovGainPerSecondLog10,
    toSafeLog10(event.before.hovGainPerSecond),
  );

  if (event.kind === "mhov-mp-execution") {
    segment.mHovCount += 1;
    return;
  }
  segment.nHovCount += 1;
  incrementRecord(segment.nHovCountsByBranch, event.branchId, 1);
  incrementRecord(
    segment.nHovBranchLevelGains,
    event.branchId,
    event.after.branchLevel - event.before.branchLevel,
  );
}

function accumulateHguEvent(
  segment: SegmentAccumulator,
  event: HoverIdleHovGainUpgradeMpAutomationEvent,
): void {
  observeSequence(segment, event.sequence);
  const purchaseCount = event.purchases.reduce(
    (sum, purchase) => sum + purchase.purchasesApplied,
    0,
  );
  segment.hguSegmentCount += 1;
  segment.hguPurchaseCount += purchaseCount;
  segment.hguAttempts += event.purchases.reduce(
    (sum, purchase) => sum + purchase.attempts,
    0,
  );
  segment.orderedEvents.push({
    sequence: event.sequence,
    kind: event.kind,
    hguPurchaseCount: purchaseCount,
  });
  for (const purchase of event.purchases) {
    segment.hovSpentByHgu = segment.hovSpentByHgu.add(purchase.hovSpent);
    for (const index of purchase.unlockedTargetIndexes) {
      segment.unlockedTargetIndexes.add(index);
    }
  }
}

function finalizeSegment(
  segment: SegmentAccumulator,
): GameplayAutomationBurstSegmentSummary | null {
  if (segment.firstSequence === null || segment.lastSequence === null) {
    return null;
  }
  return {
    firstSequence: segment.firstSequence,
    lastSequence: segment.lastSequence,
    resetEventsTotal: segment.resetEventsTotal,
    mHovCount: segment.mHovCount,
    nHovCount: segment.nHovCount,
    nHovCountsByBranch: { ...segment.nHovCountsByBranch },
    nHovBranchLevelGains: { ...segment.nHovBranchLevelGains },
    hguSegmentCount: segment.hguSegmentCount,
    hguPurchaseCount: segment.hguPurchaseCount,
    hguAttempts: segment.hguAttempts,
    hovSpentByHgu: serializeHuge(segment.hovSpentByHgu),
    unlockedTargetIndexes: [...segment.unlockedTargetIndexes].sort(
      (a, b) => a - b,
    ),
    peakHovLog10: segment.peakHovLog10,
    peakHovGainPerSecondLog10: segment.peakHovGainPerSecondLog10,
    orderedEvents: segment.orderedEvents,
  };
}

function collectHguMilestones(
  events: readonly HoverIdleHovGainUpgradeMpAutomationEvent[],
): readonly GameplayAutomationHguMilestone[] {
  const milestones: GameplayAutomationHguMilestone[] = [];
  for (const event of events) {
    for (const purchase of event.purchases) {
      if (
        purchase.purchasesApplied <= 0 ||
        purchase.lastMilestonePurchase === null
      ) {
        continue;
      }
      milestones.push({
        target: purchase.target,
        isMilestone: true,
        enhancementLevelBefore:
          purchase.lastMilestonePurchase.enhancementLevelBefore,
        enhancementLevelAfter:
          purchase.lastMilestonePurchase.enhancementLevelAfter,
      });
    }
  }
  return milestones;
}

function collectHguPulses(
  events: readonly HoverIdleHovGainUpgradeMpAutomationEvent[],
): readonly {
  readonly target: HovGainUpgradeMpTarget;
  readonly isMilestone: boolean;
}[] {
  const pulses = new Map<
    string,
    { readonly target: HovGainUpgradeMpTarget; isMilestone: boolean }
  >();
  for (const event of events) {
    for (const purchase of event.purchases) {
      if (purchase.purchasesApplied <= 0) {
        continue;
      }
      const key = toHovGainUpgradeMpTargetKey(purchase.target);
      if (key === null) {
        continue;
      }
      const current = pulses.get(key);
      pulses.set(key, {
        target: purchase.target,
        isMilestone:
          current?.isMilestone === true || purchase.milestonePurchaseCount > 0,
      });
    }
  }
  return [...pulses.values()];
}

function collectUnlockedTargetIndexes(
  events: readonly HoverIdleHovGainUpgradeMpAutomationEvent[],
): readonly HovCellGainUpgradeIndex[] {
  const indexes = new Set<HovCellGainUpgradeIndex>();
  for (const event of events) {
    for (const purchase of event.purchases) {
      for (const index of purchase.unlockedTargetIndexes) {
        indexes.add(index);
      }
    }
  }
  return [...indexes].sort((a, b) => a - b);
}

function sumHguPurchases(
  events: readonly HoverIdleHovGainUpgradeMpAutomationEvent[],
): number {
  return events.reduce(
    (eventTotal, event) =>
      eventTotal +
      event.purchases.reduce(
        (purchaseTotal, purchase) => purchaseTotal + purchase.purchasesApplied,
        0,
      ),
    0,
  );
}

function observeSequence(segment: SegmentAccumulator, sequence: number): void {
  segment.firstSequence ??= sequence;
  segment.lastSequence = sequence;
}

function incrementRecord(
  record: Partial<Record<NHovBranchId, number>>,
  key: NHovBranchId,
  value: number,
): void {
  record[key] = (record[key] ?? 0) + value;
}

function toSafeLog10(value: HugeNumber): number | null {
  if (!value.isFinite() || compareHuge(value, 0) <= 0) {
    return null;
  }
  const result = value.log10().toNumber();
  return Number.isFinite(result) ? result : null;
}

function maxNullable(
  current: number | null,
  next: number | null,
): number | null {
  if (next === null) {
    return current;
  }
  return current === null ? next : Math.max(current, next);
}
